import { z } from 'zod';
import { isoDateTimeSchema, uuidSchema } from './common.js';
/**
 * AI streams — admin-curated catalog of AI radio streams referenced by
 * venue formats (`internal.venue_formats.default_ai_streams[]`) and venue
 * playback rules (`customers.venue_playback_rules.ai_streams[]`).
 *
 * Wave 2.62 BE-2 (TASK-095d-followup-be-ai-streams). PRD §12.2 settings
 * catalog.
 *
 * Code grammar mirrors `venueFormatAiStreamCodeSchema` in `./venue-format.ts`
 * (same regex `/^[a-z][a-z0-9_-]*$/`, length 1..64) so an existing
 * `default_ai_streams[]` entry can be migrated into this catalog without
 * re-normalisation. The two columns are NOT linked by FK at the DB level —
 * this catalog is a curator's reference, deleting a stream here is gated
 * by an API-layer reference-count check.
 *
 * Five schemas:
 *   - `aiStreamRowSchema`           — single-row wire DTO
 *   - `aiStreamListResponseSchema`  — paginated list response
 *   - `aiStreamCreateRequestSchema` — POST body
 *   - `aiStreamPatchRequestSchema`  — PATCH body (code immutable)
 *   - `aiStreamListQuerySchema`     — GET query params (search, filter)
 */
/**
 * `code` grammar — mirrors `venueFormatAiStreamCodeSchema`. Kept duplicated
 * (rather than re-exported) so callers see the field labelled with its own
 * context in error messages.
 */
export const aiStreamCodeSchema = z
    .string()
    .trim()
    .min(1)
    .max(64)
    .regex(/^[a-z][a-z0-9_-]*$/, 'Lowercase slug starting with a letter; digits, "_" and "-" allowed');
/**
 * Wire DTO for a single AI-stream row.
 */
export const aiStreamRowSchema = z
    .object({
    id: uuidSchema,
    code: aiStreamCodeSchema,
    name: z.string().min(1).max(255),
    description: z.string().nullable(),
    is_active: z.boolean(),
    created_at: isoDateTimeSchema,
    updated_at: isoDateTimeSchema,
})
    .strict();
/**
 * List response — offset/limit pagination with `total` for the FE page
 * counter. Cursor is overkill for a small curated catalog.
 */
export const aiStreamListResponseSchema = z
    .object({
    items: z.array(aiStreamRowSchema),
    total: z.number().int().nonnegative(),
    limit: z.number().int().positive(),
    offset: z.number().int().nonnegative(),
})
    .strict();
export const aiStreamCreateRequestSchema = z
    .object({
    code: aiStreamCodeSchema,
    name: z.string().trim().min(1).max(255),
    description: z.string().trim().max(2000).optional(),
    is_active: z.boolean().optional(),
})
    .strict();
/**
 * PATCH body. `code` is intentionally absent — codes are immutable so
 * venue rules persisted with the old code keep resolving.
 */
export const aiStreamPatchRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(255).optional(),
    description: z.string().trim().max(2000).nullable().optional(),
    is_active: z.boolean().optional(),
})
    .strict()
    .refine((obj) => Object.keys(obj).length > 0, 'At least one field must be provided');
/**
 * GET list query — search by code/name (case-insensitive substring),
 * filter by `is_active`, offset pagination.
 */
export const aiStreamListQuerySchema = z
    .object({
    q: z.string().trim().min(1).max(255).optional(),
    is_active: z.coerce.boolean().optional(),
    limit: z.coerce.number().int().positive().max(200).default(50),
    offset: z.coerce.number().int().nonnegative().default(0),
})
    .strict();
//# sourceMappingURL=ai-stream.js.map